import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  connectedCallback() {
    super.connectedCallback?.();
    // Reflect the initial is-delete state on first render — the minus
    // button needs to show the trash icon as soon as a cart line is
    // qty=1, not only after the user has interacted with the rocker.
    const input = this.refs?.quantityInput;
    const minus = this.querySelector('.quantity-minus');
    if (input && minus && Number(input.dataset.cartLine) && parseInt(input.value) === 1) {
      minus.classList.add('is-delete');
    }
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * When quantity is 1 and in cart context, removes the item instead of
   * decrementing — and routes through the cart-line-actions "Remove"
   * button so the buyer gets the same 5s undo + save-to-wishlist UX as
   * tapping Remove directly.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value);
    const cartLine = Number(quantityInput.dataset.cartLine);

    if (currentValue === 1 && cartLine) {
      // Prefer the cart-line-actions remove handler so the buyer sees
      // the saved-to-wishlist toast + Undo window (same UX as the
      // Remove link). Falls back to a direct line removal if the row
      // (or remove button) isn't present (e.g. non-drawer contexts).
      // NB: don't include `[data-cart-line]` in the .closest() — the
      // input itself carries that attribute, so it would match the
      // input and the subsequent querySelector would find nothing.
      const row = quantityInput.closest('.cart-items__table-row, .cart-items__row');
      const removeBtn = row?.querySelector('[data-action="remove"]');
      if (removeBtn instanceof HTMLElement) {
        removeBtn.click();
        return;
      }
      quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(0, cartLine, quantityInput.dataset.cartKey));
      return;
    }

    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);
    const cartLine = Number(quantityInput.dataset.cartLine);

    // Update delete mode class on minus button for cart quantity selector
    const minusButton = this.querySelector('.quantity-minus');
    if (minusButton) {
      minusButton.classList.toggle('is-delete', newValue === 1);
    }

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, cartLine, quantityInput.dataset.cartKey));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;

    if (newValue < min && min) quantityInput.value = min;
    if (newValue > max && max) quantityInput.value = max;
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
